import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { todayISO, addDays, fmtDayLong, relDay, ORDER_TYPE } from "@/lib/format";
import { OrderStatus, Priority } from "@/components/drift/StatusBadge";
import { PageHeader, Panel, EmptyState, LoadingRows, Avatar } from "@/components/drift/common";
import { AssignDialog } from "@/components/drift/Dialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const START = 6, END = 19; // 06:00 - 19:00
const HOURS = Array.from({ length: END - START }, (_, i) => START + i);
const toMin = (t) => { if (!t) return START * 60; const [h, m] = t.split(":").map(Number); return h * 60 + (m || 0); };

/* Time blocks: a quiet card with a 2px status stripe on the left. */
const STATUS_STRIPE = { ny: "before:bg-info-dot", planlagt: "before:bg-foreground/60", paagaar: "before:bg-brand", ferdig: "before:bg-ok-dot", fakturert: "before:bg-foreground" };

export default function Planning() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const date = sp.get("date") || todayISO();
  const [data, setData] = useState(null);
  const [assign, setAssign] = useState(null);

  const load = useCallback(() => api.get(`/drift/planning?date=${date}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], unassigned: [] })), [date]);
  useEffect(() => { setData(null); load(); }, [load]);

  const setDate = (d) => setSp(d === todayISO() ? {} : { date: d });
  const total = data?.rows.reduce((s, r) => s + r.orders.length, 0) || 0;

  return (
    <div data-testid="planning-page">
      <PageHeader eyebrow="Planlegging" title={fmtDayLong(date)} subtitle={`${total} oppdrag fordelt på ${data?.rows.filter((r) => r.orders.length).length || 0} montørar`}
        actions={<div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg bg-card" onClick={() => setDate(addDays(date, -1))} aria-label="Førre dag" data-testid="planning-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-9 rounded-lg bg-card" onClick={() => setDate(todayISO())} data-testid="planning-today-button">I dag</Button>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg bg-card" onClick={() => setDate(addDays(date, 1))} aria-label="Neste dag" data-testid="planning-next-button"><ChevronRight className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-9 w-40 rounded-lg bg-card" data-testid="planning-date-input" />
        </div>} />

      {!data ? <LoadingRows n={8} header /> : (
        <div className="grid gap-5 xl:grid-cols-4">
          <Panel className="xl:col-span-3" bodyClass="p-0" testid="planning-board">
            <div className="overflow-x-auto scrollbar-thin">
              <div className="min-w-[860px]">
                <div className="grid border-b border-border/70" style={{ gridTemplateColumns: `200px repeat(${HOURS.length}, 1fr)` }}>
                  <div className="px-4 py-2 text-[12px] font-medium text-muted-foreground">Montør</div>
                  {HOURS.map((h) => <div key={h} className="border-l border-border/60 px-2 py-2 font-mono text-[11px] tabular text-muted-foreground">{String(h).padStart(2, "0")}:00</div>)}
                </div>
                {data.rows.map((row) => (
                  <div key={row.user.id} className="grid border-b border-border/60 last:border-b-0" style={{ gridTemplateColumns: `200px 1fr` }} data-testid="planning-row">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <Avatar name={row.user.name} initials={row.user.initials} tone="soft" />
                      <div className="min-w-0"><p className="truncate text-[13.5px] font-medium">{row.user.name}</p><p className="truncate text-[11.5px] text-muted-foreground">{row.user.title}</p></div>
                    </div>
                    <div className="relative h-[64px]" style={{ backgroundImage: `repeating-linear-gradient(to right, hsl(var(--border) / 0.6) 0 1px, transparent 1px ${100 / HOURS.length}%)` }}>
                      {row.orders.length === 0 && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11.5px] text-muted-foreground/70">Ledig</span>}
                      {row.orders.map((o) => {
                        const startMin = Math.max(START * 60, toMin(o.scheduled_time));
                        const dur = Math.max(1, Number(o.estimated_hours) || (o.type === "prosjekt" ? 8 : 2));
                        const left = ((startMin - START * 60) / ((END - START) * 60)) * 100;
                        const width = Math.min(100 - left, (dur * 60) / ((END - START) * 60) * 100);
                        return (
                          <button key={o.id} onClick={() => navigate(`/drift/oppdrag/${o.id}`)} title={`${o.scheduled_time || ""} ${o.title} – ${o.customer_name}`}
                            className={cn("shadow-panel absolute top-2 h-[48px] overflow-hidden rounded-lg bg-card py-1 pl-3 pr-2 text-left text-foreground transition-[box-shadow,transform] duration-150 before:absolute before:bottom-1.5 before:left-1.5 before:top-1.5 before:w-0.5 before:rounded-full hover:z-10 hover:shadow-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", STATUS_STRIPE[o.status] || STATUS_STRIPE.ny, o.priority === "akutt" && "ring-1 ring-[hsl(var(--tone-danger-dot))]")}
                            style={{ left: `${left}%`, width: `calc(${width}% - 4px)` }} data-testid="planning-order-block">
                            <p className="truncate text-[12px] font-medium leading-tight">{o.scheduled_time && <span className="tabular mr-1 font-mono text-muted-foreground">{o.scheduled_time}</span>}{o.title}</p>
                            <p className="truncate text-[11px] text-muted-foreground">{o.customer_name}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          <Panel title="Ikkje planlagt" subtitle={`${data.unassigned.length} oppdrag utan montør eller dato`} bodyClass="p-0" testid="planning-unassigned-panel">
            {data.unassigned.length === 0 ? <div className="p-4"><EmptyState compact icon={CalendarDays} title="Alt er planlagt" text="Nye oppdrag utan montør eller dato dukkar opp her." /></div> : (
              <ul className="divide-y divide-border/60">
                {data.unassigned.map((o) => (
                  <li key={o.id} className="px-4 py-3" data-testid="planning-unassigned-item">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link to={`/drift/oppdrag/${o.id}`} className="u-link block truncate text-[13.5px] font-medium">{o.title}</Link>
                        <p className="truncate text-[12.5px] text-muted-foreground">{o.customer_name} · {ORDER_TYPE[o.type]}{o.estimated_hours ? ` · ${o.estimated_hours} t` : ""}</p>
                        <div className="mt-1.5 flex items-center gap-1.5"><OrderStatus status={o.status} size="sm" /><Priority priority={o.priority} hideNormal />{o.scheduled_date && <span className="text-[11px] text-muted-foreground">{relDay(o.scheduled_date)}</span>}</div>
                      </div>
                      <Button size="sm" variant="outline" className="h-8 shrink-0 rounded-lg bg-card text-xs" onClick={() => setAssign(o)} data-testid="planning-assign-button">Planlegg</Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}

      <AssignDialog order={assign} open={!!assign} onOpenChange={(v) => !v && setAssign(null)} defaultDate={date} onSaved={load} />
    </div>
  );
}
